import React from 'react';
import ConceptTestForm from './useCaseForms/ConceptTestForm';
import ClaimsTestingForm from './useCaseForms/ClaimsTesting';
import SurveyAndFocusGroups from './useCaseForms/SurveyAndFocusGroups';
import PackagingReviewV2Form from './useCaseForms/PackagingReviewV2Form';
import PackagingVariantComparisonForm from './useCaseForms/PackagingVariantComparisonForm';
import AttributionForm from './useCaseForms/AttributionForm';
import ChannelStrategyForm from './useCaseForms/ABTestCreatives';
import ImageSurveyForm from './useCaseForms/ImageSurvey';
import TestUseCaseForm from './useCaseForms/TestUseCase';
import DrivingConceptTestForm from './useCaseForms/DrivingConceptTestForm';
import VitaminConceptTestForm from './useCaseForms/VitaminConceptTestForm';
import type { UseCaseType } from '../landing/LandingScreen';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface UseCaseFormRendererProps {
  useCaseType: UseCaseType;
  onSavePayload: (payload: Record<string, any>) => void;
  onBack: () => void;
  onFormDataChange?: (useCaseType: string, data: Record<string, any>) => void;
  initialFormData?: Record<string, any>;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
}

const UseCaseFormRenderer: React.FC<UseCaseFormRendererProps> = ({
  useCaseType,
  onSavePayload,
  onBack,
  onFormDataChange,
  initialFormData = {},
  selectedSegmentIds,
  personaFilters,
}) => {
  const commonProps = {
    onSubmit: () => {},
    selectedSegmentIds,
    personaFilters,
    onBack,
    onEditStep: () => {},
    initialFormData,
    onFormDataChange: onFormDataChange
      ? (data: Record<string, any>) => onFormDataChange(useCaseType as string, data)
      : undefined,
    onSavePayload,
  };

  const renderForm = () => {
    switch (useCaseType) {
      case 'concept-test':
        return <ConceptTestForm {...commonProps} />;
      case 'claims-testing':
        return <ClaimsTestingForm {...commonProps} />;
      case 'survey-and-focus-groups':
        return <SurveyAndFocusGroups {...commonProps} />;
      case 'packaging-review-v2':
        return <PackagingReviewV2Form {...commonProps} />;
      case 'packaging-variant-comparison':
        return <PackagingVariantComparisonForm {...commonProps} />;
      case 'attribution':
        return <AttributionForm {...commonProps} />;
      case 'channel-strategy':
        return <ChannelStrategyForm {...commonProps} />;
      case 'image-survey':
        return <ImageSurveyForm {...commonProps} />;
      case 'test-use-case':
        return <TestUseCaseForm {...commonProps} />;
      case 'driving-concept-test':
        return <DrivingConceptTestForm {...commonProps} />;
      case 'vitamin-concept-test':
        return <VitaminConceptTestForm {...commonProps} />;
      default:
        return (
          <div className="text-center py-12 text-gray-500">
            Form not available for this use case type.
          </div>
        );
    }
  };

  // Vitamin concept test uses full-screen layout
  if (useCaseType === 'vitamin-concept-test') {
    return renderForm();
  }

  return (
    <div className="mx-auto w-[80vw] max-w-4xl">
      {renderForm()}
    </div>
  );
};

export default UseCaseFormRenderer;
